import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Clock, Plus, Users, Bell, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import GroupCallDialog from "@/components/GroupCallDialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Meetings() {
  const { user } = useAuth();
  const [data, setData] = useState({ upcoming: [], active: [], past: [] });
  const [showSchedule, setShowSchedule] = useState(false);
  const [joinMeeting, setJoinMeeting] = useState(null);
  const [form, setForm] = useState({ title: "", scheduled_at: "", participants: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/meetings/mine`);
      setData(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const schedule = async () => {
    if (!form.title) return toast.error("Informe o título");
    if (!form.scheduled_at) return toast.error("Escolha data e hora");
    const dt = new Date(form.scheduled_at);
    if (dt <= new Date()) return toast.error("A data deve ser futura");
    setBusy(true);
    try {
      const emails = form.participants.split(",").map(s => s.trim()).filter(Boolean);
      const participants = [];
      for (const email of emails) {
        try {
          const r = await axios.get(`${API}/users?q=${encodeURIComponent(email)}&limit=1`);
          if (r.data?.[0]?.user_id) participants.push(r.data[0].user_id);
        } catch { /* skip */ }
      }
      const iso = dt.toISOString();
      await axios.post(`${API}/meetings/start`, {
        title: form.title, scheduled_at: iso, participants,
      });
      toast.success("Reunião agendada! Todos os participantes receberão lembrete 10min antes.");
      setShowSchedule(false);
      setForm({ title: "", scheduled_at: "", participants: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const cancel = async (m) => {
    if (!window.confirm(`Cancelar "${m.title}"?`)) return;
    try {
      await axios.post(`${API}/meetings/${m.meeting_id}/end`);
      toast.success("Reunião cancelada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startNow = (m) => setJoinMeeting({ meeting_id: m.meeting_id, title: m.title });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="meetings-heading">Reuniões</h1>
          <p className="text-muted-foreground mt-1">Chamadas em grupo com lembretes automáticos.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="meetings-instant-btn"
            onClick={() => { setShowSchedule(false); setJoinMeeting({ meeting_id: null, title: "Reunião instantânea", participants: [] }); }}
            className="rounded-full gap-1"
          >
            <Video className="w-4 h-4" /> Iniciar agora
          </Button>
          <Button data-testid="meetings-schedule-btn" onClick={() => setShowSchedule(true)} className="rounded-full gap-1">
            <Plus className="w-4 h-4" /> Agendar
          </Button>
        </div>
      </div>

      <Section label="Ao vivo" items={data.active} emptyText="Nenhuma reunião ativa agora." showJoin userId={user?.user_id} onJoin={startNow} onCancel={cancel} />
      <Section label="Próximas" items={data.upcoming} emptyText="Nada agendado — clique em Agendar." showJoin showCancel userId={user?.user_id} onJoin={startNow} onCancel={cancel} />
      <Section label="Passadas" items={data.past} emptyText="Nenhuma reunião ainda." userId={user?.user_id} onJoin={startNow} onCancel={cancel} />

      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="max-w-md" data-testid="meetings-schedule-dialog">
          <DialogHeader><DialogTitle>Agendar reunião</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título</Label>
              <Input data-testid="meeting-title" placeholder="Ex: Kickoff do projeto" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Data e hora</Label>
              <Input data-testid="meeting-datetime" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Participantes (nomes ou e-mails, separados por vírgula)</Label>
              <Input data-testid="meeting-participants" placeholder="joao@x.com, maria" value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} />
              <p className="text-[10px] text-muted-foreground mt-1">Buscamos os usuários e enviamos convite + lembrete 10min antes.</p>
            </div>
            <Button data-testid="meeting-save" onClick={schedule} disabled={busy} className="w-full rounded-full">
              {busy ? "Agendando..." : "Agendar reunião"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <GroupCallDialog
        open={!!joinMeeting}
        onOpenChange={(v) => { if (!v) { setJoinMeeting(null); load(); } }}
        meetingId={joinMeeting?.meeting_id ?? null}
        participants={joinMeeting?.participants ?? []}
        title={joinMeeting?.title ?? "Reunião"}
      />
    </div>
  );
}

/** Section rendered as stable top-level component so React doesn't remount cards on every render of Meetings. */
function Section({ label, items, emptyText, showJoin, showCancel, userId, onJoin, onCancel }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label} ({items.length})</div>
      {items.length === 0 && <Card className="p-4 text-xs text-muted-foreground text-center">{emptyText}</Card>}
      {items.map(m => (
        <Card key={m.meeting_id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`meeting-${m.meeting_id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{m.title}</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
              <span><Users className="w-3 h-3 inline mr-1" /> {(m.invited || []).length + 1} pessoas</span>
              {m.scheduled_at && <span><Clock className="w-3 h-3 inline mr-1" /> {new Date(m.scheduled_at).toLocaleString()}</span>}
              {m.reminder_sent && <Badge variant="secondary" className="text-[9px] gap-1"><Bell className="w-2.5 h-2.5" /> lembrete enviado</Badge>}
              <Badge variant="outline" className="text-[9px]">{m.status}</Badge>
            </div>
          </div>
          {showJoin && (
            <Button size="sm" data-testid={`meeting-join-${m.meeting_id}`} onClick={() => onJoin(m)} className="rounded-full gap-1">
              <Video className="w-3.5 h-3.5" /> Entrar
            </Button>
          )}
          {showCancel && m.host_user_id === userId && (
            <Button size="sm" variant="ghost" onClick={() => onCancel(m)} data-testid={`meeting-cancel-${m.meeting_id}`} className="text-destructive rounded-full">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </Card>
      ))}
    </div>
  );
}
